import { apiClient } from './client'
import type {
  Project, ProjectCreate, ProjectUpdate, ProjectListItem, Room, RoomCreate, RoomUpdate,
  FloorPlanDetectionResponse,
  WorkspaceSaveRequest, WorkspaceSaveResponse, ConduitsResponse, CircuitData,
} from '../types'

export async function listProjects(): Promise<ProjectListItem[]> {
  const { data } = await apiClient.get<ProjectListItem[]>('/api/v1/projects/')
  return data
}

export async function createProject(project: ProjectCreate): Promise<Project> {
  const { data } = await apiClient.post<Project>('/api/v1/projects/', project)
  return data
}

export async function getProject(id: string): Promise<Project> {
  const { data } = await apiClient.get<Project>(`/api/v1/projects/${id}`)
  return data
}

export async function updateProject(id: string, project: ProjectUpdate): Promise<Project> {
  const { data } = await apiClient.put<Project>(`/api/v1/projects/${id}`, project)
  return data
}

export async function addRoom(projectId: string, room: RoomCreate): Promise<Room> {
  const { data } = await apiClient.post<Room>(`/api/v1/projects/${projectId}/rooms`, room)
  return data
}

export async function updateRoom(projectId: string, roomId: string, room: RoomUpdate): Promise<Room> {
  const { data } = await apiClient.put<Room>(`/api/v1/projects/${projectId}/rooms/${roomId}`, room)
  return data
}

export async function deleteRoom(projectId: string, roomId: string): Promise<void> {
  await apiClient.delete(`/api/v1/projects/${projectId}/rooms/${roomId}`)
}

export async function detectRooms(projectId: string, imageFile: File): Promise<FloorPlanDetectionResponse> {
  const form = new FormData()
  form.append('file', imageFile)
  const { data } = await apiClient.post<{
    detected_rooms: FloorPlanDetectionResponse['detected_rooms']
    image_dimensions?: {
      width: number
      height: number
      scale_factor?: number
      coordinate_scale_x?: number
      coordinate_scale_y?: number
    }
    confidence?: number | null
    image_path: string
  }>(
    `/api/v1/projects/${projectId}/detect-rooms`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )

  const scaleX = data.image_dimensions?.coordinate_scale_x ?? 1.0
  const scaleY = data.image_dimensions?.coordinate_scale_y ?? 1.0
  const adjustedRooms = (data.detected_rooms ?? []).map(room => ({
    ...room,
    vertices: room.vertices.map(v => ({
      x: Math.round(v.x * scaleX),
      y: Math.round(v.y * scaleY),
    })),
    walls: room.walls?.map(w => ({
      ...w,
      start: { x: Math.round(w.start.x * scaleX), y: Math.round(w.start.y * scaleY) },
      end: { x: Math.round(w.end.x * scaleX), y: Math.round(w.end.y * scaleY) },
    })),
    doors: room.doors?.map(d => ({
      ...d,
      start: { x: Math.round(d.start.x * scaleX), y: Math.round(d.start.y * scaleY) },
      end: { x: Math.round(d.end.x * scaleX), y: Math.round(d.end.y * scaleY) },
    })),
    windows: room.windows?.map(w => ({
      ...w,
      start: { x: Math.round(w.start.x * scaleX), y: Math.round(w.start.y * scaleY) },
      end: { x: Math.round(w.end.x * scaleX), y: Math.round(w.end.y * scaleY) },
    })),
  }))

  return {
    detected_rooms: adjustedRooms,
    image_dimensions: {
      width: data.image_dimensions?.width ?? 1000,
      height: data.image_dimensions?.height ?? 800,
      scale_factor: data.image_dimensions?.scale_factor ?? 1.0,
      coordinate_scale_x: scaleX,
      coordinate_scale_y: scaleY,
    },
    confidence: data.confidence ?? 0,
    image_path: data.image_path,
  }
}

export async function saveWorkspaceApi(projectId: string, data: WorkspaceSaveRequest): Promise<WorkspaceSaveResponse> {
  const { data: resp } = await apiClient.put<WorkspaceSaveResponse>(
    `/api/v1/projects/${projectId}/workspace`, data
  )
  return resp
}

export async function calculateConduits(projectId: string): Promise<ConduitsResponse> {
  const { data: resp } = await apiClient.post<ConduitsResponse>(
    `/api/v1/projects/${projectId}/calculate-conduits`
  )
  return resp
}

interface CircuitResponseRaw {
  id: string
  circuit_id?: string
  name?: string
  type?: string
  rooms?: unknown[]
  protection_amps?: number
  conductor_mm2?: number
  current_a?: number
  voltage_drop_pct?: number
  color?: string
}

interface CircuitsApiResponse {
  project_id: string
  circuits: CircuitResponseRaw[]
  total_circuits: number
}

export async function getCircuits(projectId: string): Promise<CircuitData[]> {
  const { data } = await apiClient.get<CircuitsApiResponse>(
    `/api/v1/projects/${projectId}/circuits`
  )
  return data.circuits.map(c => ({
    id: String(c.circuit_id ?? c.id ?? ''),
    name: String(c.name ?? ''),
    type: String(c.type ?? ''),
    rooms: Array.isArray(c.rooms) ? c.rooms.map(String) : [],
    protection: String(c.protection_amps ?? ''),
    conductor_mm2: Number(c.conductor_mm2 ?? 0),
    amperage: Number(c.current_a ?? 0),
    voltage_drop_pct: Number(c.voltage_drop_pct ?? 0),
    color: String(c.color ?? '#000000'),
  }))
}

export async function getPersistedConduits(projectId: string): Promise<ConduitsResponse> {
  const { data } = await apiClient.get<ConduitsResponse>(
    `/api/v1/projects/${projectId}/conduits`
  )
  return data
}

interface GeocodeResponse {
  latitude: number
  longitude: number
  formatted_address: string
  map_image_path: string | null
}

export async function geocodeProject(projectId: string): Promise<GeocodeResponse> {
  const res = await apiClient.post<GeocodeResponse>(`/api/v1/projects/${projectId}/geocode`)
  return res.data
}

export async function updatePointCounts(
  projectId: string,
  roomId: string,
  data: { light_points: number; outlet_points: number; special_circuits: string[] }
): Promise<void> {
  await apiClient.put(`/api/v1/projects/${projectId}/rooms/${roomId}/point-counts`, data)
}

export async function getPlanPreview(projectId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/api/v1/projects/${projectId}/plan-preview`,
    { responseType: 'blob' }
  )
  return data
}

interface DeleteFloorResponse {
  project_id: string
  num_floors: number
  rooms_deleted: number
}

export async function deleteFloor(projectId: string, floorNumber: number): Promise<DeleteFloorResponse> {
  const { data } = await apiClient.delete<DeleteFloorResponse>(
    `/api/v1/projects/${projectId}/floors/${floorNumber}`
  )
  return data
}
